import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AVATAR_CATEGORIES,
  type AvatarCategory,
  type PickerOption,
} from "@/lib/liveavatar/options";
import { cn } from "@/lib/utils";
import { Check, ChevronLeft, ChevronRight, User } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

function AvatarThumb({ url, alt }: { url: string; alt: string }) {
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className="h-full w-full object-cover object-top"
    />
  );
}

export function AvatarPicker({
  value, onChange, label = "Avatar", options, isLoading, error,
}: {
  value?: string;
  onChange: (id: string) => void;
  label?: string;
  options: PickerOption[];
  isLoading?: boolean;
  error?: Error | null;
}) {
  const [filter, setFilter] = useState<"All" | AvatarCategory>("All");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const categories = useMemo<("All" | AvatarCategory)[]>(() => {
    const present = new Set<AvatarCategory>();
    for (const o of options) if (o.category) present.add(o.category);
    const ordered = AVATAR_CATEGORIES.filter((c) => present.has(c));
    return ["All", ...ordered];
  }, [options]);

  const filteredOptions = useMemo(
    () => (filter === "All" ? options : options.filter((o) => o.category === filter)),
    [options, filter],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [filteredOptions.length]);

  function scrollBy(dir: 1 | -1) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  function Tile({ o }: { o: PickerOption }) {
    const selected = o.id === value;
    return (
      <button
        type="button"
        onClick={() => onChange(o.id)}
        className={cn(
          "group relative flex w-28 shrink-0 snap-start flex-col overflow-hidden rounded-lg border bg-card text-left transition-all hover:shadow-md",
          selected ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/40",
        )}
      >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
          {o.preview_url ? (
            <AvatarThumb url={o.preview_url} alt={o.label} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <User className="h-5 w-5" />
            </div>
          )}
          {selected && (
            <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground shadow">
              <Check className="h-2.5 w-2.5" />
            </span>
          )}
        </div>
        <div className="px-1.5 py-1">
          <p className="truncate text-[11px] font-medium leading-tight">{o.label}</p>
          {o.sublabel && <p className="truncate text-[10px] leading-tight text-muted-foreground">{o.sublabel}</p>}
        </div>
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {!isLoading && !error && categories.length > 2 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setFilter(cat)}
              className={cn(
                "shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-all",
                filter === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-accent text-muted-foreground hover:text-foreground",
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      )}
      {error ? (
        <p className="text-xs text-destructive">{error.message || "Failed to load avatars"}</p>
      ) : isLoading ? (
        <div className="flex gap-2 overflow-x-hidden rounded-lg border bg-muted/20 p-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] w-28 shrink-0 rounded-lg" />
          ))}
        </div>
      ) : options.length === 0 ? (
        <p className="text-xs text-muted-foreground">No avatars available.</p>
      ) : filteredOptions.length === 0 ? (
        <div className="rounded-lg border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
          No avatars match this filter.{" "}
          <button
            type="button"
            onClick={() => setFilter("All")}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Clear
          </button>
        </div>
      ) : (
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-2 overflow-x-auto overflow-y-hidden rounded-lg border bg-muted/20 p-2 snap-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
          {filteredOptions.map((o) => (
            <Tile key={o.id} o={o} />
          ))}
          </div>
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll left"
              className="absolute left-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ring-1 ring-border hover:bg-background"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Scroll right"
              className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ring-1 ring-border hover:bg-background"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
